import React, { useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import LocalHospitalIcon from '@mui/icons-material/LocalHospital'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import { UserContext } from '../../../App'
import API from '../../../services/API'
import PortalRequestReviewDialog from '../../../pages/patientTransport/components/PortalRequestReviewDialog'

const POLL_MS = 30 * 1000

/**
 * Site-wide alert for PTS co-ordinators: hospital portal transport requests
 * waiting for review. Hidden on the PTS control screen, which has its own
 * flashing alert for the same queue.
 */
const PortalRequestsBanner = () => {
  const { user } = useContext(UserContext)
  const location = useLocation()
  const [pendingCount, setPendingCount] = useState(0)
  const [reviewOpen, setReviewOpen] = useState(false)

  const isCoordinator = user?.employee?.ptsAccess?.includes('CoOrdinator')
  const onControlScreen = location.pathname.startsWith('/pts/control')

  useEffect(() => {
    if (!isCoordinator) return undefined

    const loadCount = async () => {
      try {
        const res = await API.get('/pts/portal-requests/pending', {
          skipGlobalErrorHandler: true,
        })
        if (res.status === 200) {
          setPendingCount((res.data || []).length)
        }
      } catch (e) {
        console.error(e)
      }
    }

    loadCount()
    const interval = setInterval(loadCount, POLL_MS)
    return () => clearInterval(interval)
  }, [isCoordinator])

  if (!isCoordinator || onControlScreen || !pendingCount) return null

  return (
    <>
      <Box
        role="status"
        sx={{
          flexShrink: 0,
          mx: { xs: 1, md: 2 },
          mt: 1,
          mb: 0.5,
          px: { xs: 1.5, md: 2 },
          py: 0.75,
          borderRadius: 2,
          border: '1px solid rgba(230, 126, 34, 0.28)',
          background:
            'linear-gradient(135deg, rgba(255, 247, 237, 0.98) 0%, rgba(255, 255, 255, 0.96) 48%, rgba(232, 244, 248, 0.94) 100%)',
          boxShadow:
            '0 10px 28px rgba(36, 47, 64, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.7)',
          color: '#243040',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            width: 5,
            background: 'linear-gradient(180deg, #F59E0B 0%, #0E7490 100%)',
          },
          '@keyframes portalRequestPulse': {
            '0%': { boxShadow: '0 0 0 0 rgba(245, 158, 11, 0.45)' },
            '70%': { boxShadow: '0 0 0 7px rgba(245, 158, 11, 0)' },
            '100%': { boxShadow: '0 0 0 0 rgba(245, 158, 11, 0)' },
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1}
          flexWrap="wrap"
          sx={{ pl: { xs: 1, md: 1.25 } }}
        >
          <Stack direction="row" alignItems="center" gap={1.25} minWidth={0}>
            <Box
              sx={{
                width: 30,
                height: 30,
                flex: '0 0 auto',
                borderRadius: 1.5,
                display: 'grid',
                placeItems: 'center',
                color: '#92400E',
                background:
                  'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(14, 116, 144, 0.12))',
                border: '1px solid rgba(245, 158, 11, 0.22)',
                animation: 'portalRequestPulse 1.8s ease-out infinite',
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                },
              }}
            >
              <LocalHospitalIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography
              variant="body2"
              sx={{ fontWeight: 800, color: '#111827', lineHeight: 1.3 }}
            >
              {pendingCount === 1
                ? '1 new hospital transport request is waiting for review'
                : `${pendingCount} new hospital transport requests are waiting for review`}
            </Typography>
          </Stack>
          <Button
            size="small"
            variant="contained"
            endIcon={<ArrowForwardRoundedIcon />}
            onClick={() => setReviewOpen(true)}
            sx={{
              borderRadius: 1.5,
              px: 1.75,
              backgroundColor: '#111827',
              color: '#fff',
              boxShadow: '0 10px 24px rgba(17, 24, 39, 0.18)',
              textTransform: 'none',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: '#0E7490',
                boxShadow: '0 12px 28px rgba(14, 116, 144, 0.24)',
              },
            }}
          >
            Review
          </Button>
        </Stack>
      </Box>
      <PortalRequestReviewDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        onChanged={() => setPendingCount((prev) => Math.max(0, prev - 1))}
      />
    </>
  )
}

export default PortalRequestsBanner
